import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api, errMsg, fmtPrice } from "@/lib/api";
import { useSite, useSeo } from "@/context/SiteContext";
import { Link, useLang, useLocaleNavigate, useT } from "@/i18n";
import { PageHero } from "@/components/public/Shared";
import StayPicker from "@/components/public/StayPicker";
import { QuoteDetails, useDay } from "@/components/public/BookingWidget";

const OPTS = ["cleaning", "linen", "towels", "pet"];

export default function Booking() {
  const t = useT();
  const lang = useLang();
  const day = useDay();
  const navigate = useLocaleNavigate();
  const [sp] = useSearchParams();
  const { apartments, options } = useSite();
  const apt = apartments.find((a) => a.slug === sp.get("appartement"));
  const [av, setAv] = useState(null);
  const [stay, setStay] = useState({ start: sp.get("arrivee") || "", end: sp.get("depart") || "" });
  const [guests, setGuests] = useState(Number(sp.get("voyageurs")) || 2);
  const [opts, setOpts] = useState(Object.fromEntries(OPTS.map((k) => [k, sp.get(k) === "1"])));
  const [quote, setQuote] = useState(null);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "", consent: false, accept_terms: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useSeo({ title: t("Réserver votre séjour"), index: false });

  useEffect(() => { if (apt) api.get(`/public/booking/apartments/${apt.slug}/availability`).then((r) => setAv(r.data)).catch(() => setAv(null)); }, [apt]);
  useEffect(() => {
    if (!apt || !stay.start || !stay.end) { setQuote(null); return undefined; }
    const h = setTimeout(() => api.post("/public/booking/quote", { apartment: apt.slug, ...stay, guests, ...opts }).then((r) => setQuote(r.data)).catch(() => setQuote(null)), 250);
    return () => clearTimeout(h);
  }, [apt, stay, guests, opts]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!quote?.ok) return toast.error(t("Merci de choisir des dates disponibles."));
    if (!f.consent || !f.accept_terms) return toast.error(t("Merci d'accepter les conditions de réservation et la politique de confidentialité."));
    setBusy(true);
    try {
      const r = await api.post("/public/booking/bookings", { apartment: apt.slug, ...stay, guests, ...opts, ...f, locale: lang, origin_url: window.location.origin });
      if (r.data.payment?.checkout_url) { window.location.href = r.data.payment.checkout_url; return; }
      setDone(r.data.booking);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(t(errMsg(err))); } finally { setBusy(false); }
  };

  if (!apt || (av && !av.online_booking)) {
    return (
      <div>
        <PageHero eyebrow={t("Réservation")} title={t("Réserver votre séjour")} testId="booking-hero" />
        <div className="container-site py-16 text-center">
          <p className="text-charcoal/70 mb-6" data-testid="booking-unavailable">{t("La réservation en ligne n'est pas disponible pour ce séjour. Envoyez-nous une demande : nous vous répondons rapidement.")}</p>
          <Link to={`/demande-de-disponibilite${apt ? `?appartement=${apt.slug}` : ""}`} className="btn-primary">{t("Demander une disponibilité")}</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div>
        <PageHero eyebrow={t("Réservation")} title={t("Demande de réservation envoyée")} testId="booking-hero" />
        <div className="container-site py-16 max-w-2xl text-center" data-testid="booking-done">
          <CheckCircle2 size={44} className="mx-auto text-sage mb-4" />
          <p className="text-lg text-forest">{t("Merci {name} ! Votre demande de réservation {ref} a bien été enregistrée.", { name: done.first_name, ref: done.ref })}</p>
          <p className="text-charcoal/70 mt-3">{t("Nous revenons vers vous très vite pour le règlement de l'acompte. Un récapitulatif vous a été envoyé par e-mail.")}</p>
          <button type="button" onClick={() => navigate(`/appartements/${apt.slug}`)} className="btn-secondary mt-8">{t("Retour à l'appartement")}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero eyebrow={t("Réservation")} title={t("Réserver {name}", { name: apt.name })} text={t("Acompte de {p} % à la réservation, solde {d} jours avant l'arrivée.", { p: av?.deposit_percent ?? 30, d: av?.balance_days_before ?? 7 })} testId="booking-hero" />
      <form onSubmit={submit} className="container-site py-12 sm:py-16 grid lg:grid-cols-12 gap-8" data-testid="booking-form">
        <div className="lg:col-span-7 space-y-6">
          <section className="card p-5 sm:p-7">
            <h2 className="font-serif text-2xl text-forest mb-4">{t("1. Vos dates")}</h2>
            {av && <StayPicker busy={av.busy} rules={av.rules} value={stay} onChange={setStay} testId="booking-stay-picker" />}
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="label" htmlFor="bk-guests">{t("Voyageurs")}</label>
                <select id="bk-guests" className="input" value={guests} onChange={(e) => setGuests(Number(e.target.value))} data-testid="booking-guests">
                  {Array.from({ length: apt.capacity || 4 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{t("{n} personne(s)", { n })}</option>)}
                </select>
              </div>
              <fieldset>
                <legend className="label">{t("Options")}</legend>
                <div className="flex flex-wrap gap-2">
                  {options.filter((o) => OPTS.includes(o.key)).map((o) => (
                    <label key={o.key} className={`cursor-pointer px-3 py-2 rounded-full border text-xs ${opts[o.key] ? "bg-forest text-ecru border-forest" : "bg-white border-sand text-forest"}`} data-testid={`booking-opt-${o.key}`}>
                      <input type="checkbox" className="sr-only" checked={opts[o.key]} onChange={(e) => setOpts({ ...opts, [o.key]: e.target.checked })} /> {o.name}
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
          </section>
          <section className="card p-5 sm:p-7">
            <h2 className="font-serif text-2xl text-forest mb-4">{t("2. Vos coordonnées")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label" htmlFor="bk-fn">{t("Prénom *")}</label><input id="bk-fn" className="input" required value={f.first_name} onChange={set("first_name")} data-testid="booking-first-name" /></div>
              <div><label className="label" htmlFor="bk-ln">{t("Nom *")}</label><input id="bk-ln" className="input" required value={f.last_name} onChange={set("last_name")} data-testid="booking-last-name" /></div>
              <div><label className="label" htmlFor="bk-em">{t("Email *")}</label><input id="bk-em" type="email" className="input" required value={f.email} onChange={set("email")} data-testid="booking-email" /></div>
              <div><label className="label" htmlFor="bk-ph">{t("Téléphone *")}</label><input id="bk-ph" className="input" required minLength={6} value={f.phone} onChange={set("phone")} data-testid="booking-phone" /></div>
              <div className="sm:col-span-2"><label className="label" htmlFor="bk-msg">{t("Message (facultatif)")}</label><textarea id="bk-msg" className="input min-h-[100px]" value={f.message} onChange={set("message")} data-testid="booking-message" /></div>
              <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
            </div>
            <label className="flex gap-3 mt-5 text-sm text-charcoal/80 cursor-pointer"><input type="checkbox" checked={f.accept_terms} onChange={set("accept_terms")} className="mt-1" data-testid="booking-accept-terms" />
              <span>{t("J'accepte les conditions de réservation : acompte de {p} % à la réservation, solde {d} jours avant l'arrivée, caution de {c} € par empreinte bancaire.", { p: av?.deposit_percent ?? 30, d: av?.balance_days_before ?? 7, c: av?.security_deposit ?? 500 })}</span></label>
            <label className="flex gap-3 mt-3 text-sm text-charcoal/80 cursor-pointer"><input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-1" data-testid="booking-consent" />
              <span>{t("J'accepte que mes données soient utilisées pour traiter ma réservation.")} <Link to="/politique-de-confidentialite" className="underline">{t("Politique de confidentialité")}</Link></span></label>
          </section>
        </div>
        <aside className="lg:col-span-5 lg:self-start lg:sticky lg:top-28">
          <div className="card p-5 sm:p-7" data-testid="booking-summary">
            <p className="eyebrow mb-2">{t("Récapitulatif")}</p>
            <h3 className="font-serif text-2xl text-forest">{apt.name}</h3>
            {stay.start && <p className="text-sm text-charcoal/70 mt-2">{day(stay.start)} → {stay.end ? day(stay.end) : "…"}</p>}
            <div className="mt-4">{quote?.ok ? <QuoteDetails quote={quote} testId="booking-quote" /> : quote?.errors?.length ? <p className="text-sm text-red-700" data-testid="booking-quote-error">{quote.errors[0]}</p> : <p className="text-sm text-charcoal/60">{t("Choisissez vos dates pour voir le détail du prix.")}</p>}</div>
            <button type="submit" disabled={busy || !quote?.ok} className="btn-primary w-full mt-6 py-4" data-testid="booking-submit">
              {av?.online_payment ? <CreditCard size={18} /> : <Clock size={18} />}
              {busy ? t("Envoi…") : av?.online_payment ? t("Payer l'acompte en ligne") : t("Envoyer ma demande de réservation")}
            </button>
            {!av?.online_payment && <p className="text-xs text-charcoal/60 mt-3">{t("Le paiement en ligne sera bientôt disponible : nous vous contacterons pour le règlement de l'acompte.")}</p>}
          </div>
        </aside>
      </form>
    </div>
  );
}

export function BookingConfirmation() {
  const t = useT();
  const day = useDay();
  const [sp] = useSearchParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);
  const cancelled = sp.get("cancelled") === "1";
  const isSecurity = sp.get("kind") === "security";
  useSeo({ title: t("Votre réservation"), index: false });
  useEffect(() => {
    let tries = 0; let timer;
    const poll = () => api.get(`/public/booking/bookings/${sp.get("b")}`, { params: { t: sp.get("t"), session_id: sp.get("session_id") || undefined } })
      .then((r) => {
        setB(r.data);
        const waiting = sp.get("kind") === "security" ? r.data.security?.status !== "authorized" : r.data.status === "pending_payment";
        if (sp.get("session_id") && waiting && tries++ < 10) timer = setTimeout(poll, 2500);
      })
      .catch(() => setErr(true));
    poll();
    return () => clearTimeout(timer);
  }, [sp]);
  const secSt = b?.security?.status;
  const st = !b ? null
    : isSecurity ? (["authorized", "captured"].includes(secSt) ? "security_ok" : cancelled ? "security_cancelled" : "pending_payment")
    : cancelled && !["deposit_paid", "balance_paid"].includes(b.status) ? "cancelled" : b.status;
  const paid = ["deposit_paid", "balance_paid", "security_ok"].includes(st);
  const MESSAGES = {
    deposit_paid: t("Paiement reçu : votre séjour {ref} est réservé. À très bientôt au chalet !", { ref: b?.ref }),
    balance_paid: t("Paiement reçu : votre séjour {ref} est réservé. À très bientôt au chalet !", { ref: b?.ref }),
    confirmed: t("Votre séjour {ref} est confirmé. Nous vous contactons pour le règlement de l'acompte.", { ref: b?.ref }),
    request: t("Votre demande de réservation {ref} a bien été reçue. Nous revenons vers vous très vite.", { ref: b?.ref }),
    pending_payment: t("Paiement en cours de vérification…"),
    cancelled: t("Le paiement a été annulé ou la réservation n'est plus active. Vos dates ne sont pas réservées."),
    expired: t("Le paiement a été annulé ou la réservation n'est plus active. Vos dates ne sont pas réservées."),
    security_ok: t("Merci ! L'empreinte de caution de votre séjour {ref} est enregistrée. Aucun montant n'a été débité.", { ref: b?.ref }),
    security_cancelled: t("L'enregistrement de l'empreinte a été annulé. Vous pouvez réessayer depuis le lien reçu par e-mail."),
  };
  return (
    <div>
      <PageHero eyebrow={t("Réservation")} title={t("Votre réservation")} testId="booking-confirmation-hero" />
      <div className="container-site py-16 max-w-2xl text-center" data-testid="booking-confirmation">
        {err ? <p className="text-charcoal/70">{t("Réservation introuvable.")}</p> : !b ? <p className="text-charcoal/60">{t("Chargement…")}</p> : (
          <>
            {paid || st === "confirmed" ? <CheckCircle2 size={44} className="mx-auto text-sage mb-4" /> : ["cancelled", "expired", "security_cancelled"].includes(st) ? <XCircle size={44} className="mx-auto text-red-500 mb-4" /> : <Clock size={44} className="mx-auto text-gold mb-4" />}
            <p className="text-lg text-forest" data-testid="booking-confirmation-status">
              {MESSAGES[st] || MESSAGES.pending_payment}
            </p>
            <p className="text-charcoal/70 mt-3">{b.apartment_name} · {day(b.start)} → {day(b.end)}</p>
            <Link to="/" className="btn-secondary mt-8">{t("Retour à l'accueil")}</Link>
          </>
        )}
      </div>
    </div>
  );
}


// Lien durable envoyé par e-mail (solde, caution) : la session de paiement Stripe est créée au moment du clic.
export function BookingPayment() {
  const t = useT();
  const day = useDay();
  const [sp] = useSearchParams();
  const kind = sp.get("kind") === "security" ? "security" : "balance";
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSeo({ title: kind === "security" ? t("Dépôt de garantie (caution) de votre séjour") : t("Solde de votre séjour"), index: false });
  useEffect(() => {
    api.get(`/public/booking/bookings/${sp.get("b")}`, { params: { t: sp.get("t") } }).then((r) => setB(r.data)).catch(() => setErr(t("Réservation introuvable.")));
  }, [sp, t]);
  const pay = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/public/booking/bookings/${sp.get("b")}/pay`, null, { params: { t: sp.get("t"), kind } });
      window.location.href = r.data.checkout_url;
    } catch (e) { setErr(t(errMsg(e))); setBusy(false); }
  };
  const amount = kind === "security" ? b?.security?.amount : b?.quote?.balance;
  const done = kind === "security" ? ["authorized", "captured"].includes(b?.security?.status) : b?.status === "balance_paid";
  return (
    <div>
      <PageHero eyebrow={t("Réservation")} title={kind === "security" ? t("Dépôt de garantie (caution) de votre séjour") : t("Solde de votre séjour")} testId="booking-payment-hero" />
      <div className="container-site py-16 max-w-xl" data-testid="booking-payment">
        {!b && !err && <p className="text-charcoal/60 text-center">{t("Chargement…")}</p>}
        {b && (
          <div className="card p-6 sm:p-8">
            <p className="eyebrow mb-2">{b.ref}</p>
            <p className="font-serif text-2xl text-forest">{b.apartment_name}</p>
            <p className="text-charcoal/70 mt-1">{day(b.start)} → {day(b.end)}</p>
            <div className="flex items-baseline justify-between border-t border-sand mt-5 pt-5">
              <span className="text-charcoal/70">{kind === "security" ? t("Dépôt de garantie (caution, non débitée)") : t("Solde à régler")}</span>
              <span className="font-serif text-3xl text-forest" data-testid="booking-payment-amount">{fmtPrice(amount)}</span>
            </div>
            {kind === "security" && <p className="text-sm text-charcoal/60 mt-3">{t("Le montant est seulement autorisé sur votre carte, il n'est pas débité. Il est libéré après votre départ si tout est en ordre.")}</p>}
            {kind === "balance" && b.quote?.balance_due_date && <p className="text-sm text-charcoal/60 mt-3">{t("À régler avant le")} {day(b.quote.balance_due_date)}</p>}
            {done ? <p className="mt-6 text-sage font-medium flex items-center gap-2" data-testid="booking-payment-done"><CheckCircle2 size={18} /> {kind === "security" ? t("Empreinte déjà enregistrée, merci !") : t("Solde déjà réglé, merci !")}</p>
              : <button type="button" onClick={pay} disabled={busy} className="btn-primary w-full mt-6 py-4" data-testid="booking-payment-btn">
                {busy ? t("Redirection…") : kind === "security" ? t("Enregistrer l'empreinte") : t("Payer le solde")}</button>}
          </div>
        )}
        {err && <p className="text-red-600 text-sm mt-4 text-center" data-testid="booking-payment-error">{err}</p>}
      </div>
    </div>
  );
}
